import { useEffect, useRef, useState } from 'react';
import { fetchWithCorpId } from '../utils/api';
import { useParams } from 'react-router-dom';
import { Section, Field, FormLoading } from './ui/formkit';
import { useToast } from './ui/Toast';
import PageHeader from './ui/PageHeader';

const DEFAULT_ADMIN = { feature: '', description: '', value: '' };

const READONLY_INPUT = {
  background: 'var(--surface-soft)', cursor: 'not-allowed', color: 'var(--muted)',
};

function AdminForm({
  mode = 'create',
  adminId,                  // optional id; overrides URL param
  formRef,                  // optional ref attached to <form>
  hideActions = false,      // hide inline buttons
  hideChrome = false,       // drawer host already provides title/card frame
  onSuccess,
  onCancel,
}) {
  const params = useParams();
  const toast = useToast();
  const id = adminId ?? params.id;
  const isEdit = mode === 'edit';
  const [admin, setAdmin] = useState(DEFAULT_ADMIN);
  const [message, setMessage] = useState('');
  const [loadingRecord, setLoadingRecord] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  // Snapshot of the last saved state; drives the drawer's discard guard.
  // In edit mode it is set once the record loads.
  const initialSnapshotRef = useRef(isEdit ? null : JSON.stringify(DEFAULT_ADMIN));

  useEffect(() => {
    const fetchAdmin = async () => {
      if (isEdit && id) {
        setLoadingRecord(true);
        try {
          const data = await fetchWithCorpId(`/admin/${id}`);
          setAdmin(data);
          initialSnapshotRef.current = JSON.stringify(data);
        } catch (error) {
          setMessage('Could not load this setting: ' + error.message);
        } finally {
          setLoadingRecord(false);
        }
      } else {
        setLoadingRecord(false);
      }
    };
    fetchAdmin();
  }, [isEdit, id]);

  const isDirty = initialSnapshotRef.current !== null
    && JSON.stringify(admin) !== initialSnapshotRef.current;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAdmin(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage('');
    setSaving(true);
    try {
      const method = admin.id ? 'PUT' : 'POST';
      const url = admin.id ? `/admin/${admin.id}` : '/admin';
      await fetchWithCorpId(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...admin,
          corpId: localStorage.getItem('selectedCorpId') || '0',
          updateDate: new Date().toISOString(),
        }),
      });
      initialSnapshotRef.current = JSON.stringify(admin);
      toast.success(admin.id ? 'Setting updated' : 'Setting created');
      onSuccess?.();
    } catch (error) {
      setMessage(error.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formInner = loadingRecord ? (
    <FormLoading label="Loading setting…" />
  ) : (
    <form ref={formRef} onSubmit={handleSubmit} data-dirty={isDirty ? 'true' : 'false'}>
      {message && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      <Section title="Setting" description="Read-only fields are system-managed.">
        <Field label="Admin ID">
          <input type="text" name="id" value={admin.id || ''} readOnly style={READONLY_INPUT} />
        </Field>
        <Field label="Category">
          <input type="text" name="category" value={admin.category || ''} readOnly style={READONLY_INPUT} />
        </Field>
        <Field label="Feature" span={2}>
          <input type="text" name="feature" value={admin.feature || ''} readOnly style={READONLY_INPUT} />
        </Field>
        <Field label="Description" span={2}>
          <textarea name="description" value={admin.description || ''} rows={3} readOnly
            style={{ ...READONLY_INPUT, resize: 'vertical' }} />
        </Field>
      </Section>

      <Section title="Configuration" grid="1fr">
        <Field
          label={(
            <>
              Value
              <span style={{
                marginLeft: '8px', fontSize: '10px', fontWeight: 600,
                padding: '2px 7px', borderRadius: '4px',
                background: 'var(--surface-soft)', color: 'var(--coral)',
                textTransform: 'none', letterSpacing: '0.2px',
              }}>Editable</span>
            </>
          )}
          required
          hint="This is the only configurable field for this setting."
        >
          <input type="text" name="value" value={admin.value || ''} onChange={handleChange} required />
        </Field>
      </Section>

      {!hideActions && (
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>
          )}
        </div>
      )}
    </form>
  );

  // Drawer host: just the form fields (drawer provides title + footer).
  if (hideChrome) return formInner;

  return (
    <div className="page-container">
      <PageHeader
        backTo="/on/settings?tab=corporation"
        backLabel="Back to Corporation settings"
        title={mode === 'edit' ? 'Edit setting' : 'New setting'}
        subtitle="Application configuration value."
      />
      <div className="card" style={{ maxWidth: '760px' }}>
        <div className="card-body">
          {formInner}
        </div>
      </div>
    </div>
  );
}

export default AdminForm;
